import React, { useState, useEffect } from 'react';
import { useUser } from '@/shared/hooks/useUser';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useProjectsWithProgress,
  useProjectsWithAsbuilds,
  useProjectsWithDrones,
  useProjectsWithDronePhotos,
  useProjectsWithSurveillance,
  useAllUserProjects,
} from '@/shared/hooks/useUserProjects';
import { Client } from '@/types/base';
import { logout } from '@/shared/api/authentication-api';
import { useToast } from '@/hooks/use-toast';

// Import shadcn/ui components
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavLinkProps {
  href: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  isIndented?: boolean;
  onClick?: () => void;
  className?: string;
}

const NavLink: React.FC<NavLinkProps> = ({
  href,
  icon,
  children,
  isIndented = false,
  onClick,
  className = '',
}) => {
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onClick) onClick();
    navigate(href);
  };

  return (
    <Button
      variant="ghost"
      className={`inline-flex items-center whitespace-nowrap text-sm font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 w-full justify-start space-x-3 px-4 py-2 text-foreground hover:bg-accent hover:text-accent-foreground transition-colors duration-200 rounded-lg group h-auto ${
        isIndented ? 'ml-4 text-sm' : ''
      } ${className}`}
      onClick={handleClick}
    >
      <div className="w-5 h-5 text-muted-foreground group-hover:text-accent-foreground transition-colors flex-shrink-0">
        {icon}
      </div>
      <span className="font-medium text-left">{children}</span>
    </Button>
  );
};

// Helper function to get the appropriate client logo
const getClientLogo = (client: Client | null) => {
  if (!client) {
    return { type: 'fallback', url: '/static/images/iv360-p-500.png' };
  }

  // Prioritize SVG logo URL
  if (client.logo_svg_url) {
    return { type: 'svg', url: client.logo_svg_url };
  }

  // Check for light and dark logo URLs
  if (client.logo_light_url && client.logo_dark_url) {
    return { type: 'dual', lightUrl: client.logo_light_url, darkUrl: client.logo_dark_url };
  }

  // Just light logo URL
  if (client.logo_light_url) {
    return { type: 'single', url: client.logo_light_url };
  }

  // Fallback to direct fields (backward compatibility)
  if (client.logo_svg) {
    return { type: 'svg', url: client.logo_svg };
  }

  if (client.logo_light && client.logo_dark) {
    return { type: 'dual', lightUrl: client.logo_light, darkUrl: client.logo_dark };
  }

  if (client.logo_light) {
    return { type: 'single', url: client.logo_light };
  }

  // Fallback to default Ironview logo
  return { type: 'fallback', url: '/static/images/iv360-p-500.png' };
};

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { user } = useUser();
  const { currentClient } = useCurrentClient();
  const { toast } = useToast();
  const [openAccordions, setOpenAccordions] = useState<string[]>([]);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-open the accordion for the currently active section
  useEffect(() => {
    const path = location.pathname;
    const propertyWithClientMatch = path.match(/\/app\/property\/[^/]+\/\d+\/(progress|asbuilds|drones|drawings|punch-lists)/);
    const propertyLegacyMatch = path.match(/\/app\/property\/\d+\/(progress|asbuilds|drones|drawings|punch-lists)/);
    const surveillanceMatch = path.match(/\/app\/surveillance\//);
    const teamMatch = path.match(/\/app\/team\//);
    const dronePhotosMatch = path.match(/\/app\/property\/[^/]*\/\d+\/drone-photos/);

    let section: string | null = null;
    if (propertyWithClientMatch) section = propertyWithClientMatch[1];
    else if (propertyLegacyMatch) section = propertyLegacyMatch[1];
    else if (dronePhotosMatch) section = 'drone-photos';
    else if (surveillanceMatch) section = 'surveillance';
    else if (teamMatch) section = 'team';

    if (section) {
      setOpenAccordions((prev) => prev.includes(section!) ? prev : [...prev, section!]);
    }
  }, [location.pathname]);

  // Fetch projects data for the current client being viewed
  // If viewing another client's page as superuser, show that client's projects
  const clientName = currentClient?.name;
  const progressData = useProjectsWithProgress(clientName);
  const asbuildsData = useProjectsWithAsbuilds(clientName);
  const dronesData = useProjectsWithDrones(clientName);
  const dronePhotosData = useProjectsWithDronePhotos(clientName);
  const surveillanceData = useProjectsWithSurveillance(clientName);
  const allProjectsData = useAllUserProjects(clientName);

  // Helper function to get active section and project ID from current route
  const getActiveState = () => {
    const path = location.pathname;
    
    // Match patterns like /app/property/:clientName/:projectId/progress (new) or /app/property/:projectId/progress (legacy)
    const propertyWithClientMatch = path.match(/\/app\/property\/[^/]+\/(\d+)\/(progress|asbuilds|drones|drawings|punch-lists)/);
    if (propertyWithClientMatch) {
      return {
        section: propertyWithClientMatch[2],
        projectId: parseInt(propertyWithClientMatch[1]),
      };
    }
    const propertyMatch = path.match(/\/app\/property\/(\d+)\/(progress|asbuilds|drones|drawings|punch-lists)/);
    if (propertyMatch) {
      return {
        section: propertyMatch[2],
        projectId: parseInt(propertyMatch[1]),
      };
    }

    // Match patterns like /app/surveillance/:clientName/:projectName
    const surveillanceMatch = path.match(/\/app\/surveillance\/([^/]+)\/([^/]+)/);
    if (surveillanceMatch) {
      return {
        section: 'surveillance',
        projectName: decodeURIComponent(surveillanceMatch[2]),
      };
    }

    // Match patterns like /app/team/:clientName/:projectName
    const teamMatch = path.match(/\/app\/team\/([^/]+)\/([^/]+)/);
    if (teamMatch) {
      return {
        section: 'team',
        projectName: decodeURIComponent(teamMatch[2]),
      };
    }

    return { section: null, projectId: null, projectName: null };
  };

  const activeState = getActiveState();

  const progressProjects = progressData.progressProjects;
  const isLoadingProgress = progressData.isLoading;
  const asbuildsProjects = asbuildsData.asbuildsProjects;
  const isLoadingAsbuilds = asbuildsData.isLoading;
  const dronesProjects = dronesData.dronesProjects;
  const isLoadingDrones = dronesData.isLoading;
  const dronePhotosProjects = dronePhotosData.dronePhotosProjects;
  const isLoadingDronePhotos = dronePhotosData.isLoading;
  const surveillanceProjects = surveillanceData.surveillanceProjects;
  const isLoadingSurveillance = surveillanceData.isLoading;
  const allProjects = allProjectsData.allProjects;
  const isLoadingAllProjects = allProjectsData.isLoading;

  const handleAccordionChange = (values: string[]) => {
    setOpenAccordions(values);
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      // Clear localStorage token if it exists
      localStorage.removeItem('token');
      
      toast({
        title: "Success",
        description: "Logged out successfully",
        variant: 'default',
        className: 'bg-green-500 text-white',
        duration: 3000,
      });
      
      // Redirect to homepage and reload to clear user state
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      toast({
        title: "Error",
        description: "Failed to logout. Please try again.",
        variant: "destructive",
        duration: 3000,
      });
      setIsLoggingOut(false);
    }
  };

  // Get the logo info for the current client
  const logoInfo = getClientLogo(currentClient);

  const sidebarContent = (
    <div className="flex grow flex-col gap-y-5 overflow-y-auto dark:bg-[hsl(222,47%,4%)] bg-background px-6 py-4 border-r border-border">
      {/* Logo */}
      <div className="flex h-20 shrink-0 items-center justify-center py-2">
        {logoInfo.type === 'dual' ? (
          <>
            <img
              className="max-h-full w-auto max-w-full object-contain dark:hidden"
              src={logoInfo.lightUrl}
              alt={currentClient?.name || 'Client Logo'}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/static/images/iv360-p-500.png';
              }}
            />
            <img
              className="max-h-full w-auto max-w-full object-contain hidden dark:block"
              src={logoInfo.darkUrl}
              alt={currentClient?.name || 'Client Logo'}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/static/images/iv360-p-500.png';
              }}
            />
          </>
        ) : (
          <img
            className="max-h-full w-auto max-w-full object-contain"
            src={logoInfo.url || logoInfo.lightUrl || '/static/images/iv360-p-500.png'}
            alt={currentClient?.name || 'Client Logo'}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = '/static/images/iv360-p-500.png';
            }}
          />
        )}
      </div>

      {/* Navigation */}
      <nav className="flex flex-1 flex-col space-y-2">
        <div className="space-y-1">
          {/* Projects */}
          <NavLink
            href={`/app/dashboard/${user?.client_info?.name || 'dashboard'}`}
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 21H5C4.44772 21 4 20.5523 4 20V11L1 11L11.3273 1.6115C11.7087 1.26475 12.2913 1.26475 12.6727 1.6115L23 11L20 11V20C20 20.5523 19.5523 21 19 21ZM6 19H18V9.15745L12 3.7029L6 9.15745V19Z" />
              </svg>
            }
            className={`text-base ${location.pathname.includes('/app/dashboard') ? 'bg-accent text-accent-foreground' : ''}`}
          >
            Projects
          </NavLink>

          {/* Schedule a Visit */}
          <NavLink
            href="/app/schedule-visit"
            icon={
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19.903 8.586C19.8556 8.47747 19.7892 8.37825 19.707 8.293L13.707 2.293C13.6217 2.21078 13.5225 2.14441 13.414 2.097C13.384 2.083 13.352 2.075 13.32 2.064C13.2363 2.03553 13.1492 2.01837 13.061 2.013C13.04 2.011 13.021 2 13 2H6C4.897 2 4 2.897 4 4V20C4 21.103 4.897 22 6 22H18C19.103 22 20 21.103 20 20V9C20 8.979 19.989 8.96 19.987 8.938C19.9821 8.84972 19.9649 8.76255 19.936 8.679C19.926 8.647 19.917 8.616 19.903 8.586ZM16.586 8H14V5.414L16.586 8ZM6 20V4H12V9C12 9.26522 12.1054 9.51957 12.2929 9.70711C12.4804 9.89464 12.7348 10 13 10H18L18.002 20H6Z"
                  fill="currentColor"
                />
                <path d="M8 12H16V14H8V12ZM8 16H16V18H8V16ZM8 8H10V10H8V8Z" fill="currentColor" />
              </svg>
            }
            className={`text-base ${location.pathname === '/app/schedule-visit' ? 'bg-accent text-accent-foreground' : ''}`}
          >
            Schedule a Visit
          </NavLink>

          {/* Accordion Sections */}
          <Accordion
            type="multiple"
            value={openAccordions}
            onValueChange={handleAccordionChange}
            className="space-y-1"
          >
            {/* In Progress Accordion */}
            <AccordionItem value="progress" className="border-none">
              <AccordionTrigger className={`flex items-center justify-between w-full px-4 py-2 text-left hover:bg-accent transition-colors duration-200 rounded-lg group [&[data-state=open]>div>svg]:rotate-180 hover:no-underline ${
                activeState.section === 'progress' 
                  ? 'bg-accent text-accent-foreground' 
                  : 'text-foreground hover:text-accent-foreground'
              }`}>
                <div className="flex items-center space-x-3">
                  <div className={`w-5 h-5 transition-colors ${
                    activeState.section === 'progress'
                      ? 'text-accent-foreground'
                      : 'text-muted-foreground group-hover:text-accent-foreground'
                  }`}>
                    <svg
                      width="100%"
                      height="100%"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 2C6.486 2 2 6.486 2 12C2 17.514 6.486 22 12 22C17.514 22 22 17.514 22 12C22 6.486 17.514 2 12 2ZM19.931 11H13V4.069C14.7598 4.29335 16.3953 5.09574 17.6498 6.3502C18.9043 7.60466 19.7066 9.24017 19.931 11ZM4 12C4 7.928 7.061 4.564 11 4.069V12C11.003 12.1526 11.0409 12.3024 11.111 12.438C11.126 12.468 11.133 12.501 11.152 12.531L15.354 19.254C14.3038 19.7442 13.159 19.9988 12 20C7.589 20 4 16.411 4 12ZM17.052 18.196L13.805 13H19.931C19.6746 15.0376 18.6436 16.8982 17.052 18.196Z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                  <span className="font-medium">In Progress</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-0 pt-0 px-0">
                {isLoadingProgress ? (
                  <div className="px-4 py-2 text-sm text-muted-foreground">Loading...</div>
                ) : progressProjects.length > 0 ? (
                  <div className="relative">
                    <div className="absolute left-2 top-0 bottom-0 w-px bg-border" />
                    <div className="flex flex-col space-y-1">
                      {progressProjects.map((project) => {
                        const isActive = activeState.section === 'progress' && activeState.projectId === project.id;
                        return (
                          <Button
                            key={project.id}
                            variant="ghost"
                            className={`relative flex items-left justify-start space-x-3 px-4 py-2 ml-4 transition-colors duration-200 rounded-lg group h-auto text-sm ${
                              isActive
                                ? 'bg-accent/70 text-accent-foreground font-semibold'
                                : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                            }`}
                            onClick={() => navigate(`/app/property/${clientName}/${project.id}/progress`)}
                          >
                            <span className="font-medium text-left">{project.name}</span>
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-2 text-sm text-muted-foreground">No active projects</div>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* As Builds Accordion */}
            <AccordionItem value="asbuilds" className="border-none">
              <AccordionTrigger className={`flex items-center justify-between w-full px-4 py-2 text-left hover:bg-accent transition-colors duration-200 rounded-lg group [&[data-state=open]>div>svg]:rotate-180 hover:no-underline ${
                activeState.section === 'asbuilds' 
                  ? 'bg-accent text-accent-foreground' 
                  : 'text-foreground hover:text-accent-foreground'
              }`}>
                <div className="flex items-center space-x-3">
                  <div className={`w-5 h-5 transition-colors ${
                    activeState.section === 'asbuilds'
                      ? 'text-accent-foreground'
                      : 'text-muted-foreground group-hover:text-accent-foreground'
                  }`}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.382 3C12.7607 3 13.107 3.214 13.2764 3.55279L14 5H20C20.5523 5 21 5.44772 21 6V17C21 17.5523 20.5523 18 20 18H13.618C13.2393 18 12.893 17.786 12.7236 17.4472L12 16H5V22H3V3H12.382ZM11.7639 5H5V14H13.2361L14.2361 16H19V7H12.7639L11.7639 5Z" />
                    </svg>
                  </div>
                  <span className="font-medium">As Builds</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-0 pt-0 px-0">
                {isLoadingAsbuilds ? (
                  <div className="px-4 py-2 text-sm text-muted-foreground">Loading...</div>
                ) : asbuildsProjects.length > 0 ? (
                  <div className="relative">
                    <div className="absolute left-2 top-0 bottom-0 w-px bg-border" />
                    <div className="flex flex-col space-y-1">
                      {asbuildsProjects.map((project) => {
                        const isActive = activeState.section === 'asbuilds' && activeState.projectId === project.id;
                        return (
                          <Button
                            key={project.id}
                            variant="ghost"
                            className={`relative flex items-left justify-start space-x-3 px-4 py-2 ml-4 transition-colors duration-200 rounded-lg group h-auto text-sm ${
                              isActive
                                ? 'bg-accent/70 text-accent-foreground font-semibold'
                                : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                            }`}
                            onClick={() => navigate(`/app/property/${clientName}/${project.id}/asbuilds`)}
                          >
                            <span className="font-medium text-left">{project.name}</span>
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-2 text-sm text-muted-foreground">No completed projects</div>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* Post-Tension Decks (Drones) Accordion */}
            <AccordionItem value="drones" className="border-none">
              <AccordionTrigger className={`flex items-center justify-between w-full px-4 py-2 text-left hover:bg-accent transition-colors duration-200 rounded-lg group [&[data-state=open]>div>svg]:rotate-180 hover:no-underline ${
                activeState.section === 'drones' 
                  ? 'bg-accent text-accent-foreground' 
                  : 'text-foreground hover:text-accent-foreground'
              }`}>
                <div className="flex items-center space-x-3">
                  <div className={`w-5 h-5 transition-colors ${
                    activeState.section === 'drones'
                      ? 'text-accent-foreground'
                      : 'text-muted-foreground group-hover:text-accent-foreground'
                  }`}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14 8.94737L22 14V16L14 13.4737V18.8333L17 20.5V22L12.5 21L8 22V20.5L11 18.8333V13.4737L3 16V14L11 8.94737V3.5C11 2.67157 11.6716 2 12.5 2C13.3284 2 14 2.67157 14 3.5V8.94737Z" />
                    </svg>
                  </div>
                  <span className="font-medium">Post-Tension Decks</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-0 pt-0 px-0">
                {isLoadingDrones ? (
                  <div className="px-4 py-2 text-sm text-muted-foreground">Loading...</div>
                ) : dronesProjects.length > 0 ? (
                  <div className="relative">
                    <div className="absolute left-2 top-0 bottom-0 w-px bg-border" />
                    <div className="flex flex-col space-y-1">
                      {dronesProjects.map((project) => {
                        const isActive = activeState.section === 'drones' && activeState.projectId === project.id;
                        return (
                          <Button
                            key={project.id}
                            variant="ghost"
                            className={`relative flex items-left justify-start space-x-3 px-4 py-2 ml-4 transition-colors duration-200 rounded-lg group h-auto text-sm ${
                              isActive
                                ? 'bg-accent/70 text-accent-foreground font-semibold'
                                : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                            }`}
                            onClick={() => navigate(`/app/property/${clientName}/${project.id}/drones`)}
                          >
                            <span className="font-medium text-left">{project.name}</span>
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-2 text-sm text-muted-foreground">No post-tension projects</div>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* Drone Photos Accordion */}
            <AccordionItem value="drone-photos" className="border-none">
              <AccordionTrigger className="flex justify-between w-full px-4 py-2 text-left text-foreground hover:bg-accent hover:text-accent-foreground transition-colors duration-200 rounded-lg group [&[data-state=open]>div>svg]:rotate-180 hover:no-underline">
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 text-muted-foreground group-hover:text-accent-foreground transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 3V15.5L12 19L15 15.5V3H9ZM12 2C13.1046 2 14 2.89543 14 4V5H16C16.5523 5 17 5.44772 17 6V8C17 8.55228 16.5523 9 16 9H15V14.5L12 18L9 14.5V9H8C7.44772 9 7 8.55228 7 8V6C7 5.44772 7.44772 5 8 5H10V4C10 2.89543 10.8954 2 12 2ZM12 6C11.4477 6 11 6.44772 11 7C11 7.55228 11.4477 8 12 8C12.5523 8 13 7.55228 13 7C13 6.44772 12.5523 6 12 6Z" />
                    </svg>
                  </div>
                  <span className="font-medium">Drone Photos</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-0 pt-0 px-0">
                {isLoadingDronePhotos ? (
                  <div className="px-4 py-2 text-sm text-muted-foreground">Loading...</div>
                ) : dronePhotosProjects.length > 0 ? (
                  <div className="relative">
                    <div className="absolute left-2 top-0 bottom-0 w-px bg-border" />
                    <div className="flex flex-col space-y-1">
                      {dronePhotosProjects.map((project) => (
                        <Button
                          key={project.id}
                          variant="ghost"
                          className="relative flex items-left justify-start space-x-3 px-4 py-2 ml-4 text-foreground hover:bg-accent hover:text-accent-foreground transition-colors duration-200 rounded-lg group h-auto text-sm"
                          onClick={() => {
                            if (project.drone_photos_link) {
                              window.open(project.drone_photos_link, '_blank', 'noopener,noreferrer');
                            }
                          }}
                        >
                          <span className="font-medium text-left">{project.name}</span>
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-2 text-sm text-muted-foreground">No drone photo projects</div>
                )}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="surveillance" className="border-none">
              <AccordionTrigger className={`flex items-center justify-between w-full px-4 py-2 text-left hover:bg-accent transition-colors duration-200 rounded-lg group [&[data-state=open]>div>svg]:rotate-180 hover:no-underline ${
                activeState.section === 'surveillance' 
                  ? 'bg-accent text-accent-foreground' 
                  : 'text-foreground hover:text-accent-foreground'
              }`}>
                <div className="flex items-center space-x-3">
                  <div className={`w-5 h-5 transition-colors ${
                    activeState.section === 'surveillance'
                      ? 'text-accent-foreground'
                      : 'text-muted-foreground group-hover:text-accent-foreground'
                  }`}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20ZM11.0026 16L6.75999 11.7574L8.17421 10.3431L11.0026 13.1716L16.6595 7.51472L18.0737 8.92893L11.0026 16Z" />
                    </svg>
                  </div>
                  <span className="font-medium">Surveillance</span>
                </div>
              </AccordionTrigger>

              <AccordionContent className="pb-0 pt-0 px-0">
                {isLoadingSurveillance ? (
                  <div className="px-4 py-2 text-sm text-muted-foreground">Loading...</div>
                ) : surveillanceProjects.length > 0 ? (
                  <div className="relative">
                    <div className="absolute left-2 top-0 bottom-0 w-px bg-border" />
                    <div className="flex flex-col space-y-1">
                      {surveillanceProjects.map((project) => {
                        const isActive = activeState.section === 'surveillance' && 
                          activeState.projectName !== undefined &&
                          decodeURIComponent(activeState.projectName) === decodeURIComponent(project.name);
                        return (
                          <Button
                            key={project.id}
                            variant="ghost"
                            className={`relative flex items-left justify-start space-x-3 px-4 py-2 ml-4 transition-colors duration-200 rounded-lg group h-auto text-sm ${
                              isActive
                                ? 'bg-accent/70 text-accent-foreground font-semibold'
                                : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                            }`}
                            onClick={() =>
                              navigate(`/app/surveillance/${clientName}/${project.name}`)
                            }
                          >
                            <span className="font-medium text-left">{project.name}</span>
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-2 text-sm text-muted-foreground">No surveillance projects</div>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* Team Accordion */}
            <AccordionItem value="team" className="border-none">
              <AccordionTrigger className={`flex items-center justify-between w-full px-4 py-2 text-left hover:bg-accent transition-colors duration-200 rounded-lg group [&[data-state=open]>div>svg]:rotate-180 hover:no-underline ${
                activeState.section === 'team' 
                  ? 'bg-accent text-accent-foreground' 
                  : 'text-foreground hover:text-accent-foreground'
              }`}>
                <div className="flex items-center space-x-3">
                  <div className={`w-5 h-5 transition-colors ${
                    activeState.section === 'team'
                      ? 'text-accent-foreground'
                      : 'text-muted-foreground group-hover:text-accent-foreground'
                  }`}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 11C14.7614 11 17 13.2386 17 16V22H15V16C15 14.4023 13.7511 13.0963 12.1763 13.0051L12 13C10.4023 13 9.09634 14.2489 9.00509 15.8237L9 16V22H7V16C7 13.2386 9.23858 11 12 11ZM5.5 14C5.77885 14 6.05009 14.0326 6.3101 14.0942C6.14202 14.594 6.03873 15.122 6.00896 15.6693L6 16L6.0007 16.0856C5.88757 16.0456 5.76821 16.0187 5.64446 16.0069L5.5 16C4.7203 16 4.07955 16.5949 4.00687 17.3555L4 17.5V22H2V17.5C2 15.567 3.567 14 5.5 14ZM18.5 14C20.433 14 22 15.567 22 17.5V22H20V17.5C20 16.7203 19.4051 16.0796 18.6445 16.0069L18.5 16C18.3248 16 18.1566 16.03 18.0003 16.0852L18 16C18 15.3343 17.8916 14.694 17.6915 14.0956C17.9499 14.0326 18.2211 14 18.5 14ZM5.5 8C6.88071 8 8 9.11929 8 10.5C8 11.8807 6.88071 13 5.5 13C4.11929 13 3 11.8807 3 10.5C3 9.11929 4.11929 8 5.5 8ZM18.5 8C19.8807 8 21 9.11929 21 10.5C21 11.8807 19.8807 13 18.5 13C17.1193 13 16 11.8807 16 10.5C16 9.11929 17.1193 8 18.5 8ZM5.5 10C5.22386 10 5 10.2239 5 10.5C5 10.7761 5.22386 11 5.5 11C5.77614 11 6 10.7761 6 10.5C6 10.2239 5.77614 10 5.5 10ZM18.5 10C18.2239 10 18 10.2239 18 10.5C18 10.7761 18.2239 11 18.5 11C18.7761 11 19 10.7761 19 10.5C19 10.2239 18.7761 10 18.5 10ZM12 2C14.2091 2 16 3.79086 16 6C16 8.20914 14.2091 10 12 10C9.79086 10 8 8.20914 8 6C8 3.79086 9.79086 2 12 2ZM12 4C10.8954 4 10 4.89543 10 6C10 7.10457 10.8954 8 12 8C13.1046 8 14 7.10457 14 6C14 4.89543 13.1046 4 12 4Z" />
                    </svg>
                  </div>
                  <span className="font-medium">Team</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-0 pt-0 px-0">
                {isLoadingAllProjects ? (
                  <div className="px-4 py-2 text-sm text-muted-foreground">Loading...</div>
                ) : allProjects.length > 0 ? (
                  <div className="relative">
                    <div className="absolute left-2 top-0 bottom-0 w-px bg-border" />
                    <div className="flex flex-col space-y-1">
                      {allProjects.map((project) => {
                        const isActive = activeState.section === 'team' && 
                          activeState.projectName !== undefined &&
                          decodeURIComponent(activeState.projectName) === decodeURIComponent(project.name);
                        return (
                          <Button
                            key={project.id}
                            variant="ghost"
                            className={`relative flex items-left justify-start space-x-3 px-4 py-2 ml-4 transition-colors duration-200 rounded-lg group h-auto text-sm ${
                              isActive
                                ? 'bg-accent/70 text-accent-foreground font-semibold'
                                : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                            }`}
                            onClick={() =>
                              navigate(`/app/team/${clientName}/${project.name}`)
                            }
                          >
                            <span className="font-medium text-left">{project.name}</span>
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-2 text-sm text-muted-foreground">No projects</div>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* Drawings Accordion */}
            <AccordionItem value="drawings" className="border-none">
              <AccordionTrigger className={`flex items-center justify-between w-full px-4 py-2 text-left hover:bg-accent transition-colors duration-200 rounded-lg group [&[data-state=open]>div>svg]:rotate-180 hover:no-underline ${
                activeState.section === 'drawings' 
                  ? 'bg-accent text-accent-foreground' 
                  : 'text-foreground hover:text-accent-foreground'
              }`}>
                <div className="flex items-center space-x-3">
                  <div className={`w-5 h-5 transition-colors ${
                    activeState.section === 'drawings'
                      ? 'text-accent-foreground'
                      : 'text-muted-foreground group-hover:text-accent-foreground'
                  }`}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M5 8V20H9V8H5ZM3 7L7 2L11 7V22H3V7ZM19 16V14H16V12H19V10H17V8H19V6H15V20H19V18H17V16H19ZM14 4H20C20.5523 4 21 4.44772 21 5V21C21 21.5523 20.5523 22 20 22H14C13.4477 22 13 21.5523 13 21V5C13 4.44772 13.4477 4 14 4Z" />
                    </svg>
                  </div>
                  <span className="font-medium">Drawings</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-0 pt-0 px-0">
                {isLoadingAllProjects ? (
                  <div className="px-4 py-2 text-sm text-muted-foreground">Loading...</div>
                ) : allProjects.length > 0 ? (
                  <div className="relative">
                    <div className="absolute left-2 top-0 bottom-0 w-px bg-border" />
                    <div className="flex flex-col space-y-1">
                      {allProjects.map((project) => {
                        const isActive = activeState.section === 'drawings' && activeState.projectId === project.id;
                        return (
                          <Button
                            key={project.id}
                            variant="ghost"
                            className={`relative flex items-left justify-start space-x-3 px-4 py-2 ml-4 transition-colors duration-200 rounded-lg group h-auto text-sm ${
                              isActive
                                ? 'bg-accent/70 text-accent-foreground font-semibold'
                                : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                            }`}
                            onClick={() => navigate(`/app/property/${clientName}/${project.id}/drawings`)}
                          >
                            <span className="font-medium text-left">{project.name}</span>
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-2 text-sm text-muted-foreground">No projects</div>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* Punch Lists Accordion */}
            <AccordionItem value="punch-lists" className="border-none">
              <AccordionTrigger className={`flex items-center justify-between w-full px-4 py-2 text-left hover:bg-accent transition-colors duration-200 rounded-lg group [&[data-state=open]>div>svg]:rotate-180 hover:no-underline ${
                activeState.section === 'punch-lists' 
                  ? 'bg-accent text-accent-foreground' 
                  : 'text-foreground hover:text-accent-foreground'
              }`}>
                <div className="flex items-center space-x-3">
                  <div className={`w-5 h-5 transition-colors ${
                    activeState.section === 'punch-lists'
                      ? 'text-accent-foreground'
                      : 'text-muted-foreground group-hover:text-accent-foreground'
                  }`}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.5 6V3H2V21H17V18H22V6H7.5ZM7.5 8H20V16H7.5V8ZM4 19V5H5.5V17H15V19H4ZM9.5 10V12H18V10H9.5ZM9.5 13V15H15V13H9.5Z" />
                    </svg>
                  </div>
                  <span className="font-medium">Punch Lists</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-0 pt-0 px-0">
                {isLoadingAllProjects ? (
                  <div className="px-4 py-2 text-sm text-muted-foreground">Loading...</div>
                ) : allProjects.length > 0 ? (
                  <div className="relative">
                    <div className="absolute left-2 top-0 bottom-0 w-px bg-border" />
                    <div className="flex flex-col space-y-1">
                      {allProjects.map((project) => {
                        const isActive = activeState.section === 'punch-lists' && activeState.projectId === project.id;
                        return (
                          <Button
                            key={project.id}
                            variant="ghost"
                            className={`relative flex items-left justify-start space-x-3 px-4 py-2 ml-4 transition-colors duration-200 rounded-lg group h-auto text-sm ${
                              isActive
                                ? 'bg-accent/70 text-accent-foreground font-semibold'
                                : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                            }`}
                            onClick={() => navigate(`/app/property/${clientName}/${project.id}/punch-lists`)}
                          >
                            <span className="font-medium text-left">{project.name}</span>
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-2 text-sm text-muted-foreground">No projects</div>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Coming Soon Section */}
          <div className="pt-2 pb-2">
            <h3 className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Coming Soon
            </h3>
          </div>

          <Button
            variant="ghost"
            disabled
            className="w-full justify-start space-x-3 px-4 py-2 text-muted-foreground cursor-not-allowed transition-colors duration-200 rounded-lg h-auto opacity-50"
          >
            <div className="w-5 h-5 flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22 13H20V7.23792L12.0718 14.338L4 7.21594V19H14V21H3C2.44772 21 2 20.5523 2 20V4C2 3.44772 2.44772 3 3 3H21C21.5523 3 22 3.44772 22 4V13ZM4.51146 5L12.0619 11.662L19.501 5H4.51146ZM21 18H24V20H21V23H19V20H16V18H19V15H21V18Z" />
              </svg>
            </div>
            <span className="font-medium">RFI</span>
          </Button>

          <Button
            variant="ghost"
            disabled
            className="w-full justify-start space-x-3 px-4 py-2 text-muted-foreground cursor-not-allowed transition-colors duration-200 rounded-lg h-auto opacity-50"
          >
            <div className="w-5 h-5 flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3.5 1.3457C3.58425 1.3457 3.66714 1.36699 3.74096 1.4076L22.2034 11.562C22.4454 11.695 22.5337 11.9991 22.4006 12.241C22.3549 12.3241 22.2865 12.3925 22.2034 12.4382L3.74096 22.5925C3.499 22.7256 3.19497 22.6374 3.06189 22.3954C3.02129 22.3216 3 22.2387 3 22.1544V1.8457C3 1.56956 3.22386 1.3457 3.5 1.3457ZM5 4.38261V11.0001H10V13.0001H5V19.6175L18.8499 12.0001L5 4.38261Z" />
              </svg>
            </div>
            <span className="font-medium">Submittals</span>
          </Button>
        </div>

        {/* Bottom section */}
        <div className="mt-auto space-y-1">
          {/* Staff/Admin only items */}
          {user?.is_staff && (
            <>
              {/* Add New Accordion */}
              <Accordion
                type="multiple"
                value={openAccordions}
                onValueChange={handleAccordionChange}
                className="space-y-1"
              >
                <AccordionItem value="add-new" className="border-none">
                  <AccordionTrigger className="flex items-center justify-between w-full px-4 py-2 text-left text-foreground hover:bg-accent hover:text-accent-foreground transition-colors duration-200 rounded-lg group [&[data-state=open]>div>svg]:rotate-180 hover:no-underline">
                    <div className="flex items-center space-x-3">
                      <div className="w-5 h-5 text-muted-foreground group-hover:text-accent-foreground transition-colors">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M11 11V5H13V11H19V13H13V19H11V13H5V11H11Z" />
                        </svg>
                      </div>
                      <span className="font-medium">Add New</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-0 pt-0 px-0">
                    <div className="relative">
                      <div className="absolute left-2 top-0 bottom-0 w-px bg-border" />
                      <div className="flex flex-col space-y-1">
                        <Button
                          variant="ghost"
                          className="relative flex items-left justify-start space-x-3 px-4 py-2 ml-4 text-foreground hover:bg-accent hover:text-accent-foreground transition-colors duration-200 rounded-lg group h-auto text-sm"
                          onClick={() => navigate('/app/scan/add')}
                        >
                          <span className="font-medium text-left">Add Scan</span>
                        </Button>
                        <Button
                          variant="ghost"
                          className="relative flex items-left justify-start space-x-3 px-4 py-2 ml-4 text-foreground hover:bg-accent hover:text-accent-foreground transition-colors duration-200 rounded-lg group h-auto text-sm"
                          onClick={() => navigate('/app/layout/add')}
                        >
                          <span className="font-medium text-left">Add Layout</span>
                        </Button>
                        <Button
                          variant="ghost"
                          className="relative flex items-left justify-start space-x-3 px-4 py-2 ml-4 text-foreground hover:bg-accent hover:text-accent-foreground transition-colors duration-200 rounded-lg group h-auto text-sm"
                          onClick={() => navigate('/app/surveillance/create')}
                        >
                          <span className="font-medium text-left">Surveillance Report</span>
                        </Button>
                        <Button
                          variant="ghost"
                          className="relative flex items-left justify-start space-x-3 px-4 py-2 ml-4 text-foreground hover:bg-accent hover:text-accent-foreground transition-colors duration-200 rounded-lg group h-auto text-sm"
                          onClick={() => navigate('/app/drone/upload')}
                        >
                          <span className="font-medium text-left">Upload Drone Video</span>
                        </Button>
                        <Button
                          variant="ghost"
                          className={`relative flex items-left justify-start space-x-3 px-4 py-2 ml-4 text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors duration-200 rounded-lg group h-auto text-sm ${
                            location.pathname === '/app/leads/capture' ? 'bg-blue-100 text-blue-700 font-semibold' : ''
                          }`}
                          onClick={() => navigate('/app/leads/capture')}
                        >
                          <span className="font-medium text-left">Capture Lead</span>
                        </Button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              <NavLink
                href="/app/clients/all"
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 11C14.7614 11 17 13.2386 17 16V22H15V16C15 14.4023 13.7511 13.0963 12.1763 13.0051L12 13C10.4023 13 9.09634 14.2489 9.00509 15.8237L9 16V22H7V16C7 13.2386 9.23858 11 12 11ZM5.5 14C5.77885 14 6.05009 14.0326 6.3101 14.0942C6.14202 14.594 6.03873 15.122 6.00896 15.6693L6 16L6.0007 16.0856C5.88757 16.0456 5.76821 16.0187 5.64446 16.0069L5.5 16C4.7203 16 4.07955 16.5949 4.00687 17.3555L4 17.5V22H2V17.5C2 15.567 3.567 14 5.5 14ZM18.5 14C20.433 14 22 15.567 22 17.5V22H20V17.5C20 16.7203 19.4051 16.0796 18.6445 16.0069L18.5 16C18.3248 16 18.1566 16.03 18.0003 16.0852L18 16C18 15.3343 17.8916 14.694 17.6915 14.0956C17.9499 14.0326 18.2211 14 18.5 14ZM5.5 8C6.88071 8 8 9.11929 8 10.5C8 11.8807 6.88071 13 5.5 13C4.11929 13 3 11.8807 3 10.5C3 9.11929 4.11929 8 5.5 8ZM18.5 8C19.8807 8 21 9.11929 21 10.5C21 11.8807 19.8807 13 18.5 13C17.1193 13 16 11.8807 16 10.5C16 9.11929 17.1193 8 18.5 8ZM5.5 10C5.22386 10 5 10.2239 5 10.5C5 10.7761 5.22386 11 5.5 11C5.77614 11 6 10.7761 6 10.5C6 10.2239 5.77614 10 5.5 10ZM18.5 10C18.2239 10 18 10.2239 18 10.5C18 10.7761 18.2239 11 18.5 11C18.7761 11 19 10.7761 19 10.5C19 10.2239 18.7761 10 18.5 10ZM12 2C14.2091 2 16 3.79086 16 6C16 8.20914 14.2091 10 12 10C9.79086 10 8 8.20914 8 6C8 3.79086 9.79086 2 12 2ZM12 4C10.8954 4 10 4.89543 10 6C10 7.10457 10.8954 8 12 8C13.1046 8 14 7.10457 14 6C14 4.89543 13.1046 4 12 4Z" />
                  </svg>
                }
                className={`text-base ${location.pathname === '/app/clients/all' ? 'bg-accent text-accent-foreground' : ''}`}
              >
                All Clients
              </NavLink>
              <NavLink
                href="/app/projects/all"
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 3h7v7H3V3zm0 11h7v7H3v-7zm11-11h7v7h-7V3zm0 11h7v7h-7v-7z" />
                  </svg>
                }
                className={`text-base ${location.pathname === '/app/projects/all' ? 'bg-accent text-accent-foreground' : ''}`}
              >
                All Projects
              </NavLink>
              {/* Admin link - uses regular anchor for full page load to Django admin */}
              <a
                href="/admin/"
                className="inline-flex items-center whitespace-nowrap text-sm font-medium ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 w-full justify-start space-x-3 px-4 py-2 text-foreground hover:bg-accent hover:text-accent-foreground transition-colors duration-200 rounded-lg group h-auto text-base"
              >
                <div className="w-5 h-5 text-muted-foreground group-hover:text-accent-foreground transition-colors flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 1L21.5 6.5V17.5L12 23L2.5 17.5V6.5L12 1ZM12 3.311L4.5 7.65311V16.3469L12 20.689L19.5 16.3469V7.65311L12 3.311ZM12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12C16 14.2091 14.2091 16 12 16ZM12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" />
                  </svg>
                </div>
                <span className="font-medium text-left">Admin</span>
              </a>
            </>
          )}

          <NavLink
            href="/app/account"
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2ZM12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20ZM12 11C13.1046 11 14 10.1046 14 9C14 7.89543 13.1046 7 12 7C10.8954 7 10 7.89543 10 9C10 10.1046 10.8954 11 12 11ZM7.52746 16C7.77619 13.75 9.68372 12 12 12C14.3163 12 16.2238 13.75 16.4725 16H7.52746Z" />
              </svg>
            }
            className={`text-base ${location.pathname === '/app/account' ? 'bg-accent text-accent-foreground' : ''}`}
          >
            My Account
          </NavLink>

          {/* Dark/Light Mode Toggle removed — display mode managed via Account Settings */}

          <Button
            variant="ghost"
            className="w-full justify-start space-x-3 px-4 py-2 text-foreground hover:bg-accent hover:text-destructive transition-colors duration-200 rounded-lg group h-auto"
            onClick={handleLogout}
            disabled={isLoggingOut}
          >
            <div className="w-5 h-5 text-muted-foreground group-hover:text-destructive transition-colors flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M4 18H6V20H18V4H6V6H4V3C4 2.44772 4.44772 2 5 2H19C19.5523 2 20 2.44772 20 3V21C20 21.5523 19.5523 22 19 22H5C4.44772 22 4 21.5523 4 21V18ZM6 11H13V13H6V16L1 12L6 8V11Z" />
              </svg>
            </div>
            <span className="font-medium text-base">{isLoggingOut ? 'Logging out...' : 'Logout'}</span>
          </Button>
        </div>
      </nav>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
        {sidebarContent}
      </div>

      {/* Mobile Sidebar */}
      <div className={`relative z-50 lg:hidden ${isOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 flex">
          <div className="relative mr-16 flex w-full max-w-xs flex-1">
            <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
              <Button
                variant="ghost"
                size="icon"
                className="-m-2.5 p-2.5 text-foreground"
                onClick={onClose}
              >
                <span className="sr-only">Close sidebar</span>
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            </div>

            {sidebarContent}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
